import React from 'react';
import { Card } from '@/components/ui/card';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Mail, AlertCircle } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';

export default function TermsOfService() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Dynamic Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>

        {/* Header Card */}
        <Card className="mb-8 p-6 bg-gradient-to-br from-purple-50 to-slate-50 dark:from-purple-900/20 dark:to-slate-800 border-purple-200 dark:border-purple-800">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <FileText className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
                Terms of Service
              </h1>
              <p className="text-slate-600 dark:text-slate-400">
                FixMyHood
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-500 mt-1">
                Last Updated: 16 February 2026 • Pilot Phase
              </p>
            </div>
          </div>
        </Card>

        {/* Content Card */}
        <Card className="p-8 bg-white dark:bg-slate-800 shadow-lg">
          <div className="prose prose-slate dark:prose-invert max-w-none">
            
            {/* Section 1 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-purple-600 dark:text-purple-400 mr-2">1.</span>
                Acceptance of Terms
              </h2>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                Welcome to FixMyHood. These Terms of Service ("Terms," "Agreement") constitute a legally binding agreement between you ("User," "you," "your") and FixMyHood ("FixMyHood," "we," "us," "our").
              </p>
              <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-4 rounded-r-lg mb-4">
                <p className="text-slate-900 dark:text-white font-semibold mb-2">⚠️ Beta Testing Notice</p>
                <p className="text-slate-700 dark:text-slate-300 text-sm">
                  FixMyHood is currently in early-access rollout phase. We are not yet a registered (Pty) Ltd company. We plan to formalize our legal structure upon achieving product validation with early users.
                </p>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 border-l-4 border-purple-500 p-4 rounded-r-lg">
                <p className="text-slate-900 dark:text-white font-semibold mb-2">By accessing or using FixMyHood, you agree to:</p>
                <ul className="text-slate-700 dark:text-slate-300 space-y-1">
                  <li>• Be bound by these Terms</li>
                  <li>• Comply with all applicable South African laws and regulations</li>
                  <li>• Accept our Privacy Policy (incorporated by reference)</li>
                </ul>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-4 italic">
                If you do not agree to these Terms, you must not use the Service.
              </p>
            </section>

            {/* Section 2 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-purple-600 dark:text-purple-400 mr-2">2.</span>
                Description of Service
              </h2>
              <p className="text-slate-700 dark:text-slate-300 mb-4">
                FixMyHood is a subscription-based estate management platform that provides:
              </p>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <p className="font-semibold text-slate-900 dark:text-white mb-2">🏘️ Estate Management</p>
                  <p className="text-slate-700 dark:text-slate-300 text-sm">Comprehensive tools for managing residential estates and complexes</p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <p className="font-semibold text-slate-900 dark:text-white mb-2">📋 Issue Tracking</p>
                  <p className="text-slate-700 dark:text-slate-300 text-sm">Report, track, and resolve maintenance issues</p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <p className="font-semibold text-slate-900 dark:text-white mb-2">📸 Photo Documentation</p>
                  <p className="text-slate-700 dark:text-slate-300 text-sm">Upload and manage photographic evidence</p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <p className="font-semibold text-slate-900 dark:text-white mb-2">📊 Analytics</p>
                  <p className="text-slate-700 dark:text-slate-300 text-sm">Usage statistics and maintenance insights</p>
                </div>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 rounded-lg">
                <p className="font-semibold text-slate-900 dark:text-white mb-2">🎯 Phase 2 (Coming Soon)</p>
                <p className="text-slate-700 dark:text-slate-300 text-sm">Contractor marketplace connecting estates with verified contractors for work orders and payment facilitation.</p>
              </div>
            </section>

            {/* Section 3 */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-purple-600 dark:text-purple-400 mr-2">3.</span>
                Eligibility and Registration
              </h2>
              
              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-3 mt-6">
                3.1 Eligibility Requirements
              </h3>
              <ul className="space-y-2 text-slate-700 dark:text-slate-300">
                <li className="flex items-start">
                  <span className="text-purple-500 mr-2">•</span>
                  <span><strong>Age:</strong> Users under 18 should have parental consent, particularly for subscription agreements</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-500 mr-2">•</span>
                  <span><strong>Authority:</strong> If registering on behalf of an organization, you must have the authority to bind that organization</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-500 mr-2">•</span>
                  <span><strong>Geography:</strong> Service intended for use within South Africa</span>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-3 mt-6">
                3.2 Account Security
              </h3>
              <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-4 rounded-r-lg">
                <p className="text-slate-900 dark:text-white font-semibold mb-2">You are responsible for:</p>
                <ul className="text-slate-700 dark:text-slate-300 space-y-1">
                  <li>• Maintaining the confidentiality of your password</li>
                  <li>• All activities that occur under your account</li>
                  <li>• Notifying us immediately of any unauthorized access</li>
                </ul>
              </div>

              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-3 mt-6">
                3.3 Account Types
              </h3>
              <div className="space-y-3">
                <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <p className="font-semibold text-slate-900 dark:text-white">Estate Managers</p>
                  <p className="text-slate-700 dark:text-slate-300 text-sm">Manage properties, access dashboards, create maintenance reports. Requires valid subscription.</p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <p className="font-semibold text-slate-900 dark:text-white">Contractors (Phase 2)</p>
                  <p className="text-slate-700 dark:text-slate-300 text-sm">Claim work orders, receive payments. Subject to verification and vetting.</p>
                </div>
              </div>
            </section>

            {/* Section 4 - Payments */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-purple-600 dark:text-purple-400 mr-2">4.</span>
                Subscription and Payment Terms
              </h2>
              
              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-3">
                4.1 Subscription Plans
              </h3>
              <ul className="space-y-2 text-slate-700 dark:text-slate-300 mb-4">
                <li className="flex items-start">
                  <span className="text-purple-500 mr-2">•</span>
                  <span><strong>Billing Cycles:</strong> Monthly or annual subscriptions</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-500 mr-2">•</span>
                  <span><strong>Payment Methods:</strong> Credit/debit cards via Paystack or Stripe</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-500 mr-2">•</span>
                  <span><strong>Automatic Renewal:</strong> Subscriptions automatically renew unless cancelled</span>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-3 mt-6">
                4.2 Pricing and Fees
              </h3>
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4 rounded-lg mb-4">
                <ul className="text-slate-700 dark:text-slate-300 space-y-2">
                  <li>• Current pricing displayed during checkout</li>
                  <li>• Price changes with 30 days' notice to existing subscribers</li>
                  <li>• Prices exclude applicable VAT unless stated otherwise</li>
                </ul>
              </div>

              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-3 mt-6">
                4.3 Refund Policy
              </h3>
              <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded-r-lg">
                <p className="text-slate-900 dark:text-white font-semibold mb-2">General Rule</p>
                <p className="text-slate-700 dark:text-slate-300 mb-2">
                  Subscription fees are <strong>non-refundable</strong> except as required by South African Consumer Protection Act.
                </p>
                <p className="text-slate-700 dark:text-slate-300 text-sm">
                  Cancellation takes effect at the end of the current billing period (no pro-rated refunds for early cancellation).
                </p>
              </div>
            </section>

            {/* Section 5 - Acceptable Use */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-purple-600 dark:text-purple-400 mr-2">5.</span>
                Acceptable Use Policy
              </h2>
              
              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-3">
                5.1 Permitted Uses
              </h3>
              <p className="text-slate-700 dark:text-slate-300 mb-4">
                You may use FixMyHood for legitimate estate management activities, reporting maintenance issues, and communicating with contractors.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-3 mt-6">
                5.2 Prohibited Conduct
              </h3>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-lg">
                <p className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2 text-red-600 dark:text-red-400" />
                  You agree NOT to:
                </p>
                <div className="grid sm:grid-cols-2 gap-3 text-slate-700 dark:text-slate-300 text-sm">
                  <div>
                    <p className="font-semibold mb-1">Content Violations:</p>
                    <ul className="space-y-1">
                      <li>• Submit false or fraudulent information</li>
                      <li>• Upload offensive or hateful content</li>
                      <li>• Violate intellectual property rights</li>
                      <li>• Share private information without consent</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Platform Abuse:</p>
                    <ul className="space-y-1">
                      <li>• Spam or flood the platform</li>
                      <li>• Use automated tools without authorization</li>
                      <li>• Attempt unauthorized access</li>
                      <li>• Bypass security measures</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Legal Violations:</p>
                    <ul className="space-y-1">
                      <li>• Use for any unlawful purpose</li>
                      <li>• Violate South African or international laws</li>
                      <li>• Facilitate illegal activity</li>
                      <li>• Harass or threaten other users</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Commercial Restrictions:</p>
                    <ul className="space-y-1">
                      <li>• Resell the Service without authorization</li>
                      <li>• Use to compete with FixMyHood</li>
                      <li>• Advertise third-party products</li>
                    </ul>
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-3 mt-6">
                5.3 Consequences of Violations
              </h3>
              <p className="text-slate-700 dark:text-slate-300">
                Violations may result in: Warning, temporary suspension, immediate account termination, legal action, or reporting to law enforcement.
              </p>
            </section>

            {/* Section 6 - Intellectual Property */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-purple-600 dark:text-purple-400 mr-2">6.</span>
                User Content and Intellectual Property
              </h2>
              
              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-3">
                6.1 Your Content
              </h3>
              <p className="text-slate-700 dark:text-slate-300 mb-4">
                You retain ownership of all content you create or upload (issue reports, photos, estate data). By submitting content, you grant FixMyHood a worldwide, non-exclusive, royalty-free license to store, display, and process your content to provide the Service.
              </p>
              <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-4 rounded-r-lg mb-4">
                <p className="text-slate-900 dark:text-white font-semibold">
                  We do not sell or license your content to third parties for their independent use.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-3 mt-6">
                6.2 FixMyHood Intellectual Property
              </h3>
              <p className="text-slate-700 dark:text-slate-300">
                FixMyHood and all related trademarks, logos, software, and design elements are owned by FixMyHood. We grant you a limited, non-exclusive, non-transferable license to access and use the Service. You may not copy, modify, or use our trademarks without written permission.
              </p>
            </section>

            {/* Section 7 - Data Protection */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-purple-600 dark:text-purple-400 mr-2">7.</span>
                Data Protection and Privacy
              </h2>
              <p className="text-slate-700 dark:text-slate-300 mb-4">
                Our collection, use, and protection of your personal information is governed by our <Link to="/PrivacyPolicy" className="text-purple-600 dark:text-purple-400 hover:underline">Privacy Policy</Link>, which is incorporated into these Terms by reference.
              </p>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 rounded-lg">
                <p className="font-semibold text-slate-900 dark:text-white mb-2">POPIA Compliance</p>
                <p className="text-slate-700 dark:text-slate-300 text-sm">
                  We process personal information in accordance with the Protection of Personal Information Act, 2013 (POPIA). You have rights to access, correct, delete, and restrict processing of your personal information.
                </p>
              </div>
            </section>

            {/* Section 8 - Service Modifications */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-purple-600 dark:text-purple-400 mr-2">8.</span>
                Service Modifications and Availability
              </h2>
              <ul className="space-y-2 text-slate-700 dark:text-slate-300">
                <li className="flex items-start">
                  <span className="text-purple-500 mr-2">•</span>
                  <span>We may modify, suspend, or discontinue any feature at any time</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-500 mr-2">•</span>
                  <span>We strive for 99% uptime but make no guarantees</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-500 mr-2">•</span>
                  <span>Maintenance may require temporary suspension (with advance notice when possible)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-500 mr-2">•</span>
                  <span>We are not liable for service interruptions beyond our reasonable control</span>
                </li>
              </ul>
            </section>

            {/* Section 9 - Termination */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-purple-600 dark:text-purple-400 mr-2">9.</span>
                Termination
              </h2>
              
              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-3">
                9.1 Termination by You
              </h3>
              <p className="text-slate-700 dark:text-slate-300 mb-4">
                You may cancel your subscription anytime through account settings or by contacting support. Cancellation takes effect at the end of your current billing period. No refunds for unused time.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-3 mt-6">
                9.2 Termination by FixMyHood
              </h3>
              <p className="text-slate-700 dark:text-slate-300 mb-4">
                We may suspend or terminate your account immediately if you violate these Terms, engage in fraudulent activity, fail to pay fees for 30 days, or pose a security risk.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-3 mt-6">
                9.3 Effect of Termination
              </h3>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4 rounded-lg">
                <p className="text-slate-700 dark:text-slate-300">
                  Upon termination: Your access immediately ceases, you remain liable for outstanding fees, we may delete your data after 30 days (subject to legal retention), and certain provisions (liability, indemnification, governing law) survive.
                </p>
              </div>
            </section>

            {/* Section 10 - Disclaimers */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-purple-600 dark:text-purple-400 mr-2">10.</span>
                Disclaimers and Limitation of Liability
              </h2>
              
              <div className="bg-slate-100 dark:bg-slate-700 border-2 border-slate-300 dark:border-slate-600 p-5 rounded-lg mb-4">
                <p className="text-slate-900 dark:text-white font-bold mb-2 uppercase">Service "As Is"</p>
                <p className="text-slate-700 dark:text-slate-300 text-sm">
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND. We do not warrant uninterrupted, error-free service or that the Service will meet your expectations.
                </p>
              </div>

              <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-800 p-5 rounded-lg">
                <p className="text-slate-900 dark:text-white font-bold mb-2 uppercase">Limitation of Liability</p>
                <p className="text-slate-700 dark:text-slate-300 text-sm mb-3">
                  TO THE MAXIMUM EXTENT PERMITTED BY SOUTH AFRICAN LAW, FixMyHood SHALL NOT BE LIABLE for indirect, incidental, special, consequential, or punitive damages, including loss of profits, revenue, data, or business opportunities.
                </p>
                <p className="text-slate-700 dark:text-slate-300 text-sm font-semibold">
                  Our total aggregate liability for direct damages shall not exceed the lesser of: amounts paid by you in the 12 months preceding the claim, OR ZAR 10,000.
                </p>
              </div>

              <p className="text-slate-600 dark:text-slate-400 text-sm mt-4 italic">
                Nothing in these Terms excludes liability for death/injury caused by gross negligence, fraud, or liability that cannot be excluded under South African law.
              </p>
            </section>

            {/* Section 11 - Indemnification */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-purple-600 dark:text-purple-400 mr-2">11.</span>
                Indemnification
              </h2>
              <p className="text-slate-700 dark:text-slate-300 mb-4">
                You agree to indemnify, defend, and hold harmless FixMyHood, its officers, directors, employees, and service providers from any claims, liabilities, damages, and expenses (including legal fees) arising from:
              </p>
              <ul className="space-y-2 text-slate-700 dark:text-slate-300">
                <li className="flex items-start">
                  <span className="text-purple-500 mr-2">•</span>
                  <span>Your use or misuse of the Service</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-500 mr-2">•</span>
                  <span>Your violation of these Terms</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-500 mr-2">•</span>
                  <span>Your User Content or any content you submit</span>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-500 mr-2">•</span>
                  <span>Your violation of third-party rights or any laws</span>
                </li>
              </ul>
            </section>

            {/* Section 12 - Dispute Resolution */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-purple-600 dark:text-purple-400 mr-2">12.</span>
                Dispute Resolution
              </h2>
              
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <p className="font-semibold text-slate-900 dark:text-white mb-1">Governing Law</p>
                  <p className="text-slate-700 dark:text-slate-300 text-sm">These Terms are governed by the laws of the Republic of South Africa.</p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <p className="font-semibold text-slate-900 dark:text-white mb-1">Jurisdiction</p>
                  <p className="text-slate-700 dark:text-slate-300 text-sm">Any legal proceedings shall be brought in the courts of Cape Town, South Africa.</p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <p className="font-semibold text-slate-900 dark:text-white mb-1">Informal Resolution</p>
                  <p className="text-slate-700 dark:text-slate-300 text-sm">Before filing any legal claim, contact us at noah.sparkes19@gmail.com and attempt to resolve the dispute informally for 30 days.</p>
                </div>
              </div>
            </section>

            {/* Section 13 - Consumer Protection */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-purple-600 dark:text-purple-400 mr-2">13.</span>
                Consumer Protection Act Compliance
              </h2>
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4 rounded-lg">
                <p className="text-slate-700 dark:text-slate-300 mb-3">
                  These Terms comply with the South African Consumer Protection Act, 2008 (CPA). Nothing in these Terms limits your rights under the CPA.
                </p>
                <p className="font-semibold text-slate-900 dark:text-white mb-2">Cooling-Off Period</p>
                <p className="text-slate-700 dark:text-slate-300 text-sm">
                  Under the CPA, you may have the right to cancel within a specified cooling-off period. Contact us within 5 business days of subscription purchase to inquire about cancellation rights.
                </p>
                <p className="font-semibold text-slate-900 dark:text-white mb-2 mt-3">Complaints</p>
                <p className="text-slate-700 dark:text-slate-300 text-sm">
                  Lodge complaints with: <a href="mailto:noah.sparkes19@gmail.com" className="text-green-600 dark:text-green-400 hover:underline">noah.sparkes19@gmail.com</a> or National Consumer Commission at <a href="https://www.thencc.gov.za" className="text-green-600 dark:text-green-400 hover:underline" target="_blank" rel="noopener noreferrer">www.thencc.gov.za</a>
                </p>
              </div>
            </section>

            {/* Section 14 - Contractor Terms */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-purple-600 dark:text-purple-400 mr-2">14.</span>
                Contractor Terms (Phase 2 - Future)
              </h2>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 rounded-lg">
                <p className="text-slate-900 dark:text-white font-semibold mb-2">Coming Soon</p>
                <p className="text-slate-700 dark:text-slate-300 text-sm mb-3">
                  Once the contractor marketplace launches, additional terms will apply including:
                </p>
                <ul className="text-slate-700 dark:text-slate-300 text-sm space-y-1">
                  <li>• Contractor verification and background checks</li>
                  <li>• Platform fees on completed jobs</li>
                  <li>• Independent contractor relationship (not employees)</li>
                  <li>• Quality standards and dispute resolution</li>
                </ul>
                <p className="text-slate-700 dark:text-slate-300 text-sm mt-3 italic">
                  Comprehensive contractor terms will be published before Phase 2 launch.
                </p>
              </div>
            </section>

            {/* Section 15 - Changes to Terms */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-purple-600 dark:text-purple-400 mr-2">15.</span>
                Changes to These Terms
              </h2>
              <p className="text-slate-700 dark:text-slate-300 mb-4">
                We reserve the right to modify these Terms at any time. For material changes affecting your rights, we will provide at least 30 days' notice by email and prominent platform notice.
              </p>
              <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-4 rounded-r-lg">
                <p className="text-slate-900 dark:text-white font-semibold mb-2">Acceptance of Changes</p>
                <p className="text-slate-700 dark:text-slate-300 text-sm">
                  Continued use of the Service after changes take effect constitutes acceptance. If you don't agree to changes, you must stop using the Service and may cancel your subscription.
                </p>
              </div>
            </section>

            {/* Section 16 - Contact */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-purple-600 dark:text-purple-400 mr-2">16.</span>
                Contact Information
              </h2>
              <div className="p-6 bg-gradient-to-br from-purple-50 to-slate-50 dark:from-purple-900/20 dark:to-slate-700/50 rounded-lg border border-purple-200 dark:border-purple-800">
                <p className="text-slate-900 dark:text-white font-semibold mb-3">
                  For questions or notices regarding these Terms:
                </p>
                <div className="space-y-2 text-slate-700 dark:text-slate-300">
                  <p><strong>Service:</strong> FixMyHood</p>
                  <p><strong>Email:</strong> <a href="mailto:support@fixmyhood.app" className="text-purple-600 dark:text-purple-400 hover:underline">support@fixmyhood.app</a></p>
                  <p><strong>Subject Line:</strong> "Terms of Service Inquiry"</p>
                  <p className="text-sm italic">Response time: Within 5 business days</p>
                </div>
              </div>
            </section>

            {/* Section 17 - Acknowledgment */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                <span className="text-purple-600 dark:text-purple-400 mr-2">17.</span>
                Acknowledgment
              </h2>
              <div className="bg-slate-100 dark:bg-slate-700 border-2 border-slate-300 dark:border-slate-600 p-5 rounded-lg">
                <p className="text-slate-900 dark:text-white font-bold mb-3 uppercase">
                  By creating an account or using FixMyHood, you acknowledge that:
                </p>
                <ul className="space-y-2 text-slate-700 dark:text-slate-300">
                  <li>✓ You have read and understood these Terms</li>
                  <li>✓ You agree to be bound by these Terms</li>
                  <li>✓ You have the legal capacity to enter into this agreement</li>
                  <li>✓ You consent to electronic communications and agreements</li>
                  <li>✓ You understand your obligations regarding acceptable use and payment</li>
                </ul>
              </div>
            </section>

            {/* Footer */}
            <div className="mt-12 pt-6 border-t border-slate-200 dark:border-slate-700">
              <p className="text-center text-slate-600 dark:text-slate-400 text-sm mb-2">
                <strong>FixMyHood</strong> • Operating in South Africa
              </p>
              <p className="text-center text-slate-500 dark:text-slate-500 text-xs">
                Dated: 16 February 2026 • Last Reviewed: 16 February 2026 • Next Review: Upon incorporation or 16 February 2027
              </p>
              <p className="text-center text-slate-500 dark:text-slate-500 text-xs mt-3">
                UI icons by <a href="https://www.flaticon.com/free-icons/ui" target="_blank" rel="noopener noreferrer" className="hover:underline">Iamkikirizky - Flaticon</a>
              </p>
            </div>

          </div>
        </Card>

        {/* Quick Links Footer */}
        <div className="mt-6 flex justify-center gap-4 text-sm">
          <Link
            to="/PrivacyPolicy"
            className="text-purple-600 dark:text-purple-400 hover:underline"
          >
            Privacy Policy
          </Link>
          {isAuthenticated && (
            <>
              <span className="text-slate-400">•</span>
              <Link
                to="/Settings"
                className="text-purple-600 dark:text-purple-400 hover:underline"
              >
                Account Settings
              </Link>
            </>
          )}
          {!isAuthenticated && (
            <>
              <span className="text-slate-400">•</span>
              <Link
                to="/Login"
                className="text-purple-600 dark:text-purple-400 hover:underline"
              >
                Sign In
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
